import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { generateCoffeeBeanRecommendations } from '../../../services/openaiService';

const AIRecommendationsTab = ({ language }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const content = {
    en: {
      title: 'AI-Powered Recommendations',
      subtitle: 'Personalized coffee bean suggestions based on your purchase history and preferences',
      generate: 'Generate Recommendations',
      regenerate: 'Refresh Recommendations',
      analysis: 'Purchase Pattern Analysis',
      insights: 'Key Insights',
      recommendations: 'Recommended Coffee Beans',
      matchScore: 'Match Score',
      budgetFit: 'Budget Fit',
      viewDetails: 'View Details',
      loading: 'Analyzing your purchase history...',
      noData: 'No purchase history available',
      noDataDesc: 'Start ordering coffee beans to get personalized recommendations',
      withinBudget: 'Within Budget',
      slightlyAbove: 'Slightly Above',
      premiumOption: 'Premium Option',
      price: 'Price',
      available: 'Available',
      supplier: 'Supplier',
      metadata: 'Analysis Summary'
    },
    zh: {
      title: 'AI智能推荐',
      subtitle: '基于您的购买历史和偏好的个性化咖啡豆建议',
      generate: '生成推荐',
      regenerate: '刷新推荐',
      analysis: '购买模式分析',
      insights: '关键见解',
      recommendations: '推荐的咖啡豆',
      matchScore: '匹配分数',
      budgetFit: '预算匹配',
      viewDetails: '查看详情',
      loading: '正在分析您的购买历史...',
      noData: '暂无购买历史',
      noDataDesc: '开始订购咖啡豆以获得个性化推荐',
      withinBudget: '预算内',
      slightlyAbove: '略高于预算',
      premiumOption: '高端选择',
      price: '价格',
      available: '可用',
      supplier: '供应商',
      metadata: '分析摘要'
    }
  };

  const t = content?.[language];

  const handleGenerateRecommendations = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await generateCoffeeBeanRecommendations(language);
      setRecommendations(result);
    } catch (err) {
      setError(err?.message || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getBudgetFitColor = (budgetFit) => {
    const colors = {
      within_budget: 'text-success bg-success/10',
      slightly_above: 'text-warning bg-warning/10',
      premium_option: 'text-primary bg-primary/10'
    };
    return colors?.[budgetFit] || 'text-muted-foreground bg-muted';
  };

  const getBudgetFitLabel = (budgetFit) => {
    const labels = {
      within_budget: t?.withinBudget,
      slightly_above: t?.slightlyAbove,
      premium_option: t?.premiumOption
    };
    return labels?.[budgetFit] || budgetFit;
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-primary';
    if (score >= 60) return 'text-warning';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground text-lg">{t?.loading}</p>
        <p className="text-sm text-muted-foreground mt-2">
          {language === 'en' ? 'This may take a few moments...' : '这可能需要几分钟...'}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={handleGenerateRecommendations} variant="outline">
          {language === 'en' ? 'Try Again' : '重试'}
        </Button>
      </div>
    );
  }

  if (!recommendations) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="Sparkles" size={40} className="text-primary" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-foreground mb-2">{t?.title}</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t?.subtitle}</p>
        <Button 
          onClick={handleGenerateRecommendations}
          iconName="Sparkles"
          size="lg"
        >
          {t?.generate}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-1">{t?.title}</h2>
          <p className="text-sm text-muted-foreground">
            {language === 'en' 
              ? `Generated ${new Date(recommendations?.metadata?.generatedAt)?.toLocaleString()}` 
              : `生成于 ${new Date(recommendations?.metadata?.generatedAt)?.toLocaleString()}`}
          </p>
        </div>
        <Button 
          onClick={handleGenerateRecommendations}
          variant="outline"
          iconName="RefreshCw"
          loading={loading}
        >
          {t?.regenerate}
        </Button>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="Brain" size={24} className="text-primary mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{t?.analysis}</h3>
            <p className="text-foreground leading-relaxed">{recommendations?.analysis}</p>
          </div>
        </div>
      </div>

      {recommendations?.metadata && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'en' ? 'Total Orders' : '总订单数'}
            </p>
            <p className="text-2xl font-bold text-foreground">{recommendations?.metadata?.totalOrders}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'en' ? 'Favorites' : '收藏数'}
            </p>
            <p className="text-2xl font-bold text-foreground">{recommendations?.metadata?.totalFavorites}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'en' ? 'Avg Spending' : '平均支出'}
            </p>
            <p className="text-2xl font-bold text-foreground">${recommendations?.metadata?.avgSpending}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'en' ? 'Avg Price/kg' : '平均价格/公斤'}
            </p>
            <p className="text-2xl font-bold text-foreground">${recommendations?.metadata?.avgPricePerKg}</p>
          </div>
        </div>
      )}

      {recommendations?.insights && recommendations?.insights?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Lightbulb" size={20} className="text-warning" />
            <h3 className="text-lg font-semibold text-foreground">{t?.insights}</h3>
          </div>
          <ul className="space-y-2">
            {recommendations?.insights?.map((insight, index) => (
              <li key={index} className="flex items-start gap-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-1 flex-shrink-0" />
                <span className="text-foreground">{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-xl font-heading font-bold text-foreground mb-4">{t?.recommendations}</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {recommendations?.recommendations?.map((rec, index) => {
            const bean = rec?.bean;
            if (!bean) return null;

            return (
              <div key={index} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-smooth">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground mb-1">
                        {language === 'en' ? bean?.nameEn : bean?.nameZh}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en' ? bean?.country?.nameEn : bean?.country?.nameZh}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getMatchScoreColor(rec?.matchScore)}`}>
                        {rec?.matchScore}%
                      </div>
                      <p className="text-xs text-muted-foreground">{t?.matchScore}</p>
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-3 mb-3">
                    <p className="text-sm text-foreground leading-relaxed">{rec?.reason}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t?.price}</p>
                      <p className="text-lg font-semibold text-primary">${bean?.priceUsdPerKg}/kg</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t?.available}</p>
                      <p className="text-lg font-semibold text-foreground">{bean?.availableQtyKg} kg</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBudgetFitColor(rec?.budgetFit)}`}>
                      {getBudgetFitLabel(rec?.budgetFit)}
                    </span>
                    {bean?.grade && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                        {bean?.grade}
                      </span>
                    )}
                  </div>

                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => navigate(`/bean-detail?id=${bean?.id}`)}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {t?.viewDetails}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIRecommendationsTab;